// Pointer event handling for Hexcrawl

import type { Point, Stroke, StrokePoint, Hex, HexLayout } from "../types.ts";
import { pixelToHex } from "../hex/coordinates.ts";

export interface PointerState {
  isDown: boolean;
  position: Point;
  startPosition: Point;
  pointerId: number | null;
}

type PointerEventType = "down" | "move" | "up" | "tap" | "hover" | "doubletap" | "pan";

export interface PointerEventData {
  position: Point;
  hex: Hex | null;
  button: number;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
}

type PointerCallback = (event: PointerEventData) => void;
type PanCallback = (delta: Point) => void;
type PanEndCallback = (totalDelta: Point) => void;
type StrokeCallback = (stroke: Stroke) => void;
type SubdivisionStrokeCallback = (stroke: Stroke) => void;

/**
 * Handles pointer events and converts them to high-level interactions
 */
export class PointerHandler {
  private state: PointerState = {
    isDown: false,
    position: { x: 0, y: 0 },
    startPosition: { x: 0, y: 0 },
    pointerId: null,
  };

  private listeners: Map<PointerEventType, Set<PointerCallback>> = new Map();
  private strokeListeners: Set<StrokeCallback> = new Set();
  private subdivisionStrokeListeners: Set<SubdivisionStrokeCallback> = new Set();
  private panListeners: Set<PanCallback> = new Set();
  private panEndListeners: Set<PanEndCallback> = new Set();
  private zoomListeners: Set<(zoomDelta: number, center: Point, modifiers: { ctrlKey: boolean; shiftKey: boolean; altKey: boolean }) => void> = new Set();
  private element: HTMLElement;
  private layout: HexLayout;

  // Stroke capture
  private currentStroke: StrokePoint[] = [];
  private minSampleDistance = 3; // Minimum pixels between samples

  // Subdivision mode (right-click draws instead of panning)
  private subdivisionMode = false;
  private isSubdivisionStroke = false;
  private subdivisionStroke: StrokePoint[] = [];

  // Double-tap detection
  private lastTapTime = 0;
  private lastTapPosition: Point = { x: 0, y: 0 };
  private doubleTapThreshold = 300; // ms
  private doubleTapDistance = 20; // pixels

  // Pan state (right-click or middle-click drag)
  private isPanning = false;
  private panStartScreenPosition: Point = { x: 0, y: 0 }; // Screen coords (not affected by transform)
  private panTotalDelta: Point = { x: 0, y: 0 };

  constructor(element: HTMLElement, layout: HexLayout) {
    this.element = element;
    this.layout = layout;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.element.addEventListener("pointerdown", this.handlePointerDown);
    this.element.addEventListener("pointermove", this.handlePointerMove);
    this.element.addEventListener("pointerup", this.handlePointerUp);
    this.element.addEventListener("pointerleave", this.handlePointerUp);
    this.element.addEventListener("pointercancel", this.handlePointerUp);

    // Prevent context menu on right-click (including with modifier keys)
    this.element.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }, { capture: true });

    // Handle scroll wheel for zoom
    this.element.addEventListener("wheel", this.handleWheel, { passive: false });
  }

  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const position = {
      x: e.clientX - this.element.getBoundingClientRect().left,
      y: e.clientY - this.element.getBoundingClientRect().top,
    };
    // Normalize delta (different browsers report different values)
    const delta = e.deltaY > 0 ? -1 : 1;
    const modifiers = {
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
    };
    for (const callback of this.zoomListeners) {
      callback(delta, position, modifiers);
    }
  };

  private handlePointerDown = (e: PointerEvent): void => {
    const position = this.getPosition(e);

    // Capture modifier keys
    this.modifiers = {
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
    };

    this.state.isDown = true;
    this.state.pointerId = e.pointerId;
    this.state.startPosition = position;
    this.state.position = position;

    // Check for pan gesture (right-click button=2, middle-click button=1)
    // But if subdivision mode is enabled, right-click captures a subdivision stroke instead
    // Exception: ctrl/alt+right-click should be handled by main app for quick carve shapes
    if (e.button === 2 && this.subdivisionMode && !e.ctrlKey && !e.altKey) {
      console.log("Starting subdivision stroke");
      this.isSubdivisionStroke = true;
      this.subdivisionStroke = [
        {
          x: position.x,
          y: position.y,
          pressure: e.pressure,
          timestamp: e.timeStamp,
        },
      ];
      this.element.setPointerCapture(e.pointerId);
      return;
    }

    if (e.button === 2) {
      console.log("Right-click - subdivision mode:", this.subdivisionMode, "shift:", e.shiftKey, "alt:", e.altKey);
    }

    // Quick carve mode (ctrl/alt + right-click when subdivision mode enabled)
    // Note: shift+right-click is reserved by browsers to force-show context menu
    // Skip panning and let main app handle this via the "down" event
    if (e.button === 2 && this.subdivisionMode && (e.ctrlKey || e.altKey)) {
      console.log("Quick carve mode - emitting down event");
      this.element.setPointerCapture(e.pointerId);
      this.emit("down", position, e.button);
      return;
    }

    if (e.button === 2 || e.button === 1) {
      this.isPanning = true;
      // Use screen coordinates to avoid jitter from CSS transforms
      this.panStartScreenPosition = { x: e.clientX, y: e.clientY };
      this.panTotalDelta = { x: 0, y: 0 };
      this.element.setPointerCapture(e.pointerId);
      return;
    }

    // Skip stroke recording when modifier keys are held (quick shape mode)
    // The main app will handle these via the "down" event
    // Note: using ctrl instead of shift for consistency (shift+right-click is reserved by browsers)
    if (e.ctrlKey || e.altKey) {
      this.currentStroke = []; // Empty stroke so nothing fires on up
      this.element.setPointerCapture(e.pointerId);
      this.emit("down", position, e.button);
      return;
    }

    // Start stroke capture (left-click only)
    this.currentStroke = [
      {
        x: position.x,
        y: position.y,
        pressure: e.pressure,
        timestamp: e.timeStamp,
      },
    ];

    // Capture pointer for drag tracking
    this.element.setPointerCapture(e.pointerId);

    this.emit("down", position, e.button);
  };

  private handlePointerMove = (e: PointerEvent): void => {
    const position = this.getPosition(e);
    this.state.position = position;

    // Update modifier keys
    this.modifiers = {
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
    };

    // Handle subdivision stroke capture
    if (this.isSubdivisionStroke) {
      const last = this.subdivisionStroke[this.subdivisionStroke.length - 1];
      const dist = Math.hypot(position.x - last.x, position.y - last.y);
      if (dist >= this.minSampleDistance) {
        this.subdivisionStroke.push({
          x: position.x,
          y: position.y,
          pressure: e.pressure,
          timestamp: e.timeStamp,
        });
      }
      this.emit("move", position, e.button);
      return;
    }

    // Handle panning - use screen coordinates to avoid jitter from CSS transforms
    if (this.isPanning) {
      const screenPos = { x: e.clientX, y: e.clientY };
      const totalDelta: Point = {
        x: screenPos.x - this.panStartScreenPosition.x,
        y: screenPos.y - this.panStartScreenPosition.y,
      };
      // Emit the change since last frame
      const frameDelta: Point = {
        x: totalDelta.x - this.panTotalDelta.x,
        y: totalDelta.y - this.panTotalDelta.y,
      };
      this.panTotalDelta = totalDelta;
      this.emitPan(frameDelta);
      return;
    }

    if (this.state.isDown) {
      // Sample point if far enough from last (only if recording a stroke)
      if (this.currentStroke.length > 0) {
        const last = this.currentStroke[this.currentStroke.length - 1];
        const dist = Math.hypot(position.x - last.x, position.y - last.y);

        if (dist >= this.minSampleDistance) {
          this.currentStroke.push({
            x: position.x,
            y: position.y,
            pressure: e.pressure,
            timestamp: e.timeStamp,
          });
        }
      }

      // Always emit move when dragging (needed for quick shape preview)
      this.emit("move", position, e.button);
    } else {
      this.emit("hover", position, e.button);
    }
  };

  private handlePointerUp = (e: PointerEvent): void => {
    if (!this.state.isDown) return;

    const position = this.getPosition(e);
    this.state.position = position;
    this.state.isDown = false;

    // Release pointer capture
    if (this.state.pointerId !== null) {
      this.element.releasePointerCapture(this.state.pointerId);
    }
    this.state.pointerId = null;

    // End subdivision stroke if active
    if (this.isSubdivisionStroke) {
      this.isSubdivisionStroke = false;
      if (this.subdivisionStroke.length > 0) {
        const stroke: Stroke = {
          points: this.subdivisionStroke,
          isClosed: this.checkIfClosed(this.subdivisionStroke),
        };
        this.emitSubdivisionStroke(stroke);
      }
      this.subdivisionStroke = [];
      this.emit("up", position, e.button);
      return;
    }

    // End panning if active
    if (this.isPanning) {
      this.isPanning = false;
      this.emitPanEnd(this.panTotalDelta);
      return;
    }

    // Check for tap (minimal movement)
    const startDist = Math.hypot(
      position.x - this.state.startPosition.x,
      position.y - this.state.startPosition.y
    );

    if (startDist < 10 && this.currentStroke.length < 5) {
      // This is a tap
      const now = e.timeStamp;
      const timeSinceLastTap = now - this.lastTapTime;
      const distFromLastTap = Math.hypot(
        position.x - this.lastTapPosition.x,
        position.y - this.lastTapPosition.y
      );

      if (
        timeSinceLastTap < this.doubleTapThreshold &&
        distFromLastTap < this.doubleTapDistance
      ) {
        // Double tap!
        this.emit("doubletap", position, e.button);
        this.lastTapTime = 0; // Reset to avoid triple-tap
      } else {
        // Single tap
        this.emit("tap", position, e.button);
        this.lastTapTime = now;
        this.lastTapPosition = position;
      }
    } else if (this.currentStroke.length > 0) {
      // This was a stroke - emit it (only if we have points)
      const stroke: Stroke = {
        points: this.currentStroke,
        isClosed: this.checkIfClosed(this.currentStroke),
      };
      this.emitStroke(stroke);
    }

    this.emit("up", position, e.button);
    this.currentStroke = [];
  };

  private checkIfClosed(points: StrokePoint[]): boolean {
    if (points.length < 10) return false;

    const first = points[0];
    const last = points[points.length - 1];

    // Calculate bounding box size
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const size = Math.max(
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys)
    );

    // Check if start and end are close relative to shape size
    const distance = Math.hypot(last.x - first.x, last.y - first.y);
    return distance < size * 0.15;
  }

  private getPosition(e: PointerEvent): Point {
    const rect = this.element.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  // Store modifier key state from last event
  private modifiers = { shiftKey: false, ctrlKey: false, altKey: false };

  private emit(event: PointerEventType, position: Point, button: number): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const hex = this.getHexAtPosition(position);
      for (const callback of callbacks) {
        callback({
          position,
          hex,
          button,
          ...this.modifiers,
        });
      }
    }
  }

  private emitStroke(stroke: Stroke): void {
    for (const callback of this.strokeListeners) {
      callback(stroke);
    }
  }

  private emitSubdivisionStroke(stroke: Stroke): void {
    for (const callback of this.subdivisionStrokeListeners) {
      callback(stroke);
    }
  }

  private emitPan(delta: Point): void {
    for (const callback of this.panListeners) {
      callback(delta);
    }
  }

  private emitPanEnd(totalDelta: Point): void {
    for (const callback of this.panEndListeners) {
      callback(totalDelta);
    }
  }

  /**
   * Get the hex under a given pixel position
   */
  getHexAtPosition(position: Point): Hex {
    return pixelToHex(position, this.layout);
  }

  /**
   * Get hex under current pointer position
   */
  getHexUnderPointer(): Hex | null {
    if (this.state.position.x === 0 && this.state.position.y === 0) {
      return null;
    }
    return this.getHexAtPosition(this.state.position);
  }

  /**
   * Register a listener for pointer events
   */
  on(event: PointerEventType, callback: PointerCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Remove a listener
   */
  off(event: PointerEventType, callback: PointerCallback): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Register a listener for completed strokes
   */
  onStroke(callback: StrokeCallback): void {
    this.strokeListeners.add(callback);
  }

  /**
   * Register a listener for subdivision strokes (right-click drawing when subdivision mode is enabled)
   */
  onSubdivisionStroke(callback: SubdivisionStrokeCallback): void {
    this.subdivisionStrokeListeners.add(callback);
  }

  /**
   * Remove a subdivision stroke listener
   */
  offSubdivisionStroke(callback: SubdivisionStrokeCallback): void {
    this.subdivisionStrokeListeners.delete(callback);
  }

  /**
   * Enable or disable subdivision mode (right-click draws instead of panning)
   */
  setSubdivisionMode(enabled: boolean): void {
    this.subdivisionMode = enabled;
  }

  /**
   * Check if subdivision mode is active
   */
  isSubdivisionModeActive(): boolean {
    return this.subdivisionMode;
  }

  /**
   * Get the current subdivision stroke points (while drawing)
   */
  getSubdivisionStroke(): StrokePoint[] {
    return this.subdivisionStroke;
  }

  /**
   * Remove a stroke listener
   */
  offStroke(callback: StrokeCallback): void {
    this.strokeListeners.delete(callback);
  }

  /**
   * Register a listener for pan events (right-click or middle-click drag)
   */
  onPan(callback: PanCallback): void {
    this.panListeners.add(callback);
  }

  /**
   * Remove a pan listener
   */
  offPan(callback: PanCallback): void {
    this.panListeners.delete(callback);
  }

  /**
   * Register a listener for pan end events (when pan gesture completes)
   */
  onPanEnd(callback: PanEndCallback): void {
    this.panEndListeners.add(callback);
  }

  /**
   * Remove a pan end listener
   */
  offPanEnd(callback: PanEndCallback): void {
    this.panEndListeners.delete(callback);
  }

  /**
   * Register a listener for zoom events (scroll wheel)
   */
  onZoom(callback: (zoomDelta: number, center: Point, modifiers: { ctrlKey: boolean; shiftKey: boolean; altKey: boolean }) => void): void {
    this.zoomListeners.add(callback);
  }

  /**
   * Remove a zoom listener
   */
  offZoom(callback: (zoomDelta: number, center: Point, modifiers: { ctrlKey: boolean; shiftKey: boolean; altKey: boolean }) => void): void {
    this.zoomListeners.delete(callback);
  }

  /**
   * Get the current stroke points (while drawing)
   */
  getCurrentStroke(): StrokePoint[] {
    return this.currentStroke;
  }

  /**
   * Check if pointer is currently down
   */
  isPointerDown(): boolean {
    return this.state.isDown;
  }

  /**
   * Get current pointer position
   */
  getPosition_(): Point {
    return { ...this.state.position };
  }

  /**
   * Update the hex layout (e.g., when grid moves)
   */
  setLayout(layout: HexLayout): void {
    this.layout = layout;
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    this.element.removeEventListener("pointerdown", this.handlePointerDown);
    this.element.removeEventListener("pointermove", this.handlePointerMove);
    this.element.removeEventListener("pointerup", this.handlePointerUp);
    this.element.removeEventListener("pointerleave", this.handlePointerUp);
    this.element.removeEventListener("pointercancel", this.handlePointerUp);
    this.listeners.clear();
    this.strokeListeners.clear();
  }
}
